/**
 * COMPTABILITÉ — LES SOURCES REÇUES.
 *
 * Un document bien lu n'a besoin de personne : il tient sur une ligne. C'est
 * l'ANOMALIE qui mérite de la place, parce qu'elle attend une décision humaine.
 * L'écran est donc construit en deux étages, dans cet ordre, quelle que soit la
 * vue choisie :
 *   1. les documents À TRAITER — une fiche chacun, avec la raison écrite ;
 *   2. les documents déjà lus — une table dense, une ligne chacun.
 *
 * ═══ POURQUOI AUCUN FILTRE DE DATE ═══
 * `listDocuments` filtre sur `business_date` dès qu'une borne est fournie : un
 * document dont la nuit n'a PAS pu être déduite (nuit ambiguë, période absente)
 * disparaîtrait alors de l'écran — exactement le document qu'on cherche. La
 * liste est donc bornée par un nombre, jamais par des dates, et le serveur rend
 * les documents sans nuit EN PREMIER.
 *
 * ═══ CE QUE L'ÉCRAN NE DEVINE PAS ═══
 * Aucune raison n'est fabriquée. Quand le lecteur n'a transmis ni note ni
 * anomalie, l'écran le DIT et renvoie au détail du document, plutôt que d'écrire
 * une phrase générique qui n'apprend rien à qui doit la traiter.
 *
 * ═══ LE FILTRE PAR FOURNISSEUR (`?fournisseur=`) ═══
 * Les tuiles ZAP et Shotgun de l'accueil mènent ici, filtrées. Le filtre vit
 * dans l'adresse comme la vue — un Retour depuis la fiche d'un document
 * retrouve la même liste — et se retire d'un clic sur sa puce. L'API ne sait
 * pas encore filtrer par fournisseur : le tri se fait ici, sur une fenêtre
 * élargie au plafond du serveur (cinq cents documents), parce qu'une annexe
 * ZAP arrive par vingtaines et repousserait sinon une reddition Shotgun hors
 * de la liste. L'écran DIT sur combien de documents il a cherché.
 *
 * ROUTES CONSOMMÉES (`apps/api/src/routes/finance.ts`) :
 *   GET  /api/v1/finance/sources?limit=N         finance.source.read
 *   POST /api/v1/finance/sources/:id/parse      finance.source.manage
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { FileSearch, Mail, RotateCcw, X } from 'lucide-react'
import { Link, useNavigationCount } from '../router.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { SegmentedControl } from '../admin/pickers.js'
import { adminMutate, usePermissions, useToast } from '../admin/primitives.js'
import { Checkbox } from '../admin/form.js'
import {
  SOURCE_PARSE_LABELS, SOURCE_PARSE_PILL, SOURCE_PROVIDER_LABELS,
  heureParis, ilYA, nuitCourte,
  type SourceProvider,
} from './ClosingCommon.js'
import { EtatBloc, entier, estObjet, texte, useRessource } from './RevenueShared.js'
import { BarreEcart, useSelectionDocs } from './SourcesEcart.js'
import { DepotRapports } from './SourcesDepot.js'
import { fournisseurDepuis } from './sources-adresse.js'
import { remplacerRecherche } from './adresse-ecran.js'
import { VoirSource, type DocVisible } from './SourceViewer.js'
import './finance.css'
import './finance-revenue.css'
import './finance-sources.css'

/* ═══════════════════════ le vocabulaire du fournisseur ═══════════════════ */

/**
 * Les types de rapport admis en base (migration 1757700000000). Un code absent
 * de cette table s'affiche TEL QUEL : inventer un libellé pour un type inconnu
 * ferait passer une nouveauté du fournisseur pour une catégorie maîtrisée.
 */
const TYPES_RAPPORT: Record<string, string> = {
  ZAP_REGISTER_SUMMARY: 'Synthèse des résultats',
  ZAP_OTHER: 'Autre rapport de caisse',
  CLOTURE_CAISSES: 'Clôture de caisses',
  OFFERTS: 'Rapport d’offerts',
  SHOTGUN_EVENT_REPORT: 'Reddition de comptes billetterie',
  LEGACY_PHYSICAL_CLOSE: 'Classeur de clôture (historique)',
  LEGACY_REVENUE_TRACK: 'Suivi de recette (historique)',
  LEGACY_ANNUAL: 'Récapitulatif annuel (historique)',
  INCONNU: 'Type non déterminé',
}

export function libelleType(code: string | null): string {
  if (code === null) return 'Type non transmis'
  return TYPES_RAPPORT[code] ?? code
}

export function libelleProvenance(code: string): string {
  return (SOURCE_PROVIDER_LABELS as Record<string, string | undefined>)[code] ?? code
}

export function libelleEtat(code: string): string {
  return (SOURCE_PARSE_LABELS as Record<string, string | undefined>)[code] ?? code
}

export function pastilleEtat(code: string): string {
  return (SOURCE_PARSE_PILL as Record<string, string | undefined>)[code] ?? 'lc-pill lc-pill--idle'
}

/* ═══════════════════════ ce que l'écran lit sur le réseau ════════════════ */

/** L'origine d'un document arrivé par la boîte mail des sources. */
export interface MailOrigine {
  expediteur: string | null
  sujet: string | null
  identifiant: string | null
}

export interface DocRecu {
  id: string
  provider: string
  reportType: string | null
  /** La nuit commerciale. `null` = pas encore rattaché, et c'est une question. */
  nuit: string | null
  recuLe: string | null
  fichier: string
  etat: string
  note: string | null
  deposePar: string | null
  /** Version de l'analyseur, si la liste la transmet. */
  analyseur: string | null
  /** Nombre d'anomalies, si la liste le transmet. `null` = non transmis. */
  anomalies: number | null
  mail: MailOrigine | null
  /** Décidé par le SERVEUR (hors annexes ZAP et rapports écartés). `null` = non transmis. */
  attention: boolean | null
  /** Écarté par un humain (entretien de caisse, test) : il ne compte plus nulle part. */
  ecarte: boolean
  motifEcart: string | null
  /** Une annexe ZAP (doseurs, cartes…) : une preuve, jamais une tâche. `null` sinon. */
  annexe: string | null
}

/**
 * L'origine « mail » est lue de façon TOLÉRANTE : le canal e-mail est écrit en
 * parallèle et peut la servir à plat ou sous un objet. Aucune de ces clés n'est
 * obligatoire — un dépôt manuel n'a pas de mail d'origine, et c'est normal.
 */
function lireMail(o: Record<string, unknown>): MailOrigine | null {
  const src = estObjet(o['email']) ? o['email'] : o
  const expediteur = texte(src['sender']) ?? texte(src['email_sender']) ?? texte(src['from']) ?? texte(src['email_from'])
  const sujet = texte(src['subject']) ?? texte(src['email_subject'])
  const identifiant = texte(src['gmail_message_id']) ?? texte(src['message_id'])
  if (expediteur === null && sujet === null && identifiant === null) return null
  return { expediteur, sujet, identifiant }
}

export function lireDoc(o: Record<string, unknown>): DocRecu | null {
  const id = texte(o['id'])
  const fichier = texte(o['filename'])
  if (id === null || fichier === null) return null
  return {
    id,
    provider: texte(o['provider']) ?? 'AUTRE',
    reportType: texte(o['report_type']),
    nuit: texte(o['business_date']),
    recuLe: texte(o['received_at']),
    fichier,
    etat: texte(o['parse_status']) ?? 'RECEIVED',
    note: texte(o['parse_note']),
    deposePar: texte(o['uploaded_by_name']),
    analyseur: texte(o['parser_version']) ?? texte(o['last_parser_version']),
    anomalies: entier(o['anomaly_count']) ?? entier(o['unmapped_count']),
    mail: lireMail(o),
    attention: typeof o['needs_attention'] === 'boolean' ? o['needs_attention'] : null,
    ecarte: o['ignored'] === true,
    motifEcart: texte(o['ignored_reason']),
    annexe: texte(o['report_type']) === 'ZAP_OTHER' ? (texte(o['annex_kind']) ?? 'AUTRE') : null,
  }
}

/** Le format d'une annexe ZAP, dit en clair. */
const ANNEXES: Record<string, string> = {
  DOSEURS: 'Annexe · doseurs', CARTES: 'Annexe · cartes serveur', TIROIRS: 'Annexe · tiroirs-caisses',
  PRODUITS: 'Annexe · produits', CAISSE: 'Annexe · détail de caisse', AUTRE: 'Annexe · TVA ou autre',
}
/** Le type affiché : une annexe dit ce qu'elle contient, pas « autre rapport ». */
const typeAffiche = (d: DocRecu): string => (d.annexe !== null ? ANNEXES[d.annexe] ?? 'Annexe ZAP' : libelleType(d.reportType))

function lireDocuments(v: unknown): DocRecu[] | null {
  const brut = Array.isArray(v)
    ? v
    : estObjet(v)
      ? ((['documents', 'sources', 'items'].map((c) => v[c]).find((x) => Array.isArray(x)) as unknown[] | undefined) ?? null)
      : null
  if (brut === null) return null
  const out: DocRecu[] = []
  for (const x of brut) {
    if (!estObjet(x)) continue
    const d = lireDoc(x)
    if (d !== null) out.push(d)
  }
  return out
}

/* ═══════════════════════ les vues ═══════════════════════ */

/**
 * Les quatre filtres du domaine, plus « Tout ». Ce cinquième n'est pas une
 * commodité : `RECEIVED` et `PARSING` n'appartiennent à AUCUN des quatre, et
 * sans lui un document reçu mais jamais lu n'aurait aucun écran où apparaître.
 */
type Vue = 'a_verifier' | 'importes' | 'doublons' | 'erreurs' | 'tout'

/** Un document que personne ne reprend ne deviendra jamais un chiffre. */
export function aTraiter(d: DocRecu): boolean {
  // La décision du serveur quand il la donne : une annexe ZAP (détail par
  // caisse, doseurs, TVA…) est une PREUVE conservée, pas une tâche, et un
  // rapport écarté ne demande plus rien. Chaque mail ZAP créait sinon vingt
  // cartes « à vérifier ».
  if (d.attention !== null) return d.attention
  return !d.ecarte && (d.etat === 'NEEDS_REVIEW' || d.etat === 'FAILED')
}

function dansLaVue(d: DocRecu, vue: Vue): boolean {
  if (vue === 'a_verifier') return aTraiter(d) && d.etat === 'NEEDS_REVIEW'
  if (vue === 'importes') return d.etat === 'PARSED'
  if (vue === 'doublons') return d.etat === 'DUPLICATE'
  if (vue === 'erreurs') return aTraiter(d) && d.etat === 'FAILED'
  return true
}

function vueDepuisUrl(): Vue {
  const v = new URLSearchParams(window.location.search).get('vue')
  return v === 'importes' || v === 'doublons' || v === 'erreurs' || v === 'tout' ? v : 'a_verifier'
}

/* ═══════════════════════ le filtre par fournisseur ═══════════════════════ */

/** Le paramètre et ses valeurs sont définis dans `sources-adresse.ts`. */
function fournisseurDepuisUrl(): SourceProvider | null {
  return fournisseurDepuis(new URLSearchParams(window.location.search).get('fournisseur'))
}

/**
 * L'adresse suit les filtres, en REMPLAÇANT l'entrée d'historique : changer
 * de vue n'est pas changer de page, et le Retour doit quitter la liste en un
 * clic (`adresse-ecran.ts` dit pourquoi ce n'est pas `redirect`).
 */
function ecrireAdresse(vue: Vue, fournisseur: SourceProvider | null): void {
  const q = new URLSearchParams()
  if (vue !== 'a_verifier') q.set('vue', vue)
  if (fournisseur !== null) q.set('fournisseur', fournisseur)
  remplacerRecherche('/comptabilite/sources', q)
}

/**
 * LA RAISON, TOUJOURS. Un document « à vérifier » sans raison affichée renvoie
 * la personne au fichier PDF et à sa patience. Quand le lecteur n'a rien
 * transmis, on l'écrit : c'est un défaut de l'analyseur, pas une fatalité, et
 * la phrase dit où aller chercher la suite.
 */
export function raisonCourte(d: DocRecu): string {
  if (d.note !== null) return d.note
  if (d.nuit === null) {
    return 'Ce document n’est rattaché à aucune nuit commerciale : tant qu’il ne l’est pas, il n’alimente aucune clôture.'
  }
  if (d.etat === 'FAILED') {
    return 'Le lecteur s’est arrêté sans transmettre de raison. Ouvrez le document : le détail de la lecture porte ce qu’il a vu avant de renoncer.'
  }
  return 'Aucune raison n’a été transmise avec ce document. Ouvrez-le : le détail de la lecture dit, ligne par ligne, ce qui n’a pas été compris.'
}

/* ═══════════════════════ l'écran ═══════════════════════ */

/** La liste est bornée par un NOMBRE, jamais par des dates (en-tête du fichier). */
const PLAFOND = 200
/** Filtrée sur un fournisseur : le plafond du serveur (`listDocuments`, 500). */
const PLAFOND_FOURNISSEUR = 500

/** Ce que la visionneuse doit savoir d'un document reçu. */
const visible = (d: DocRecu): DocVisible => ({
  id: d.id, filename: d.fichier, provider: d.provider, report_type: d.reportType,
  business_date: d.nuit, received_at: d.recuLe,
})

export function FinanceSourcesPage() {
  const toast = useToast()
  const { permissions, loading } = usePermissions()
  const peutGerer = !loading && permissions.includes('finance.source.manage')

  const [vue, setVue] = useState<Vue>(vueDepuisUrl)
  const [fournisseur, setFournisseur] = useState<SourceProvider | null>(fournisseurDepuisUrl)
  const [relecture, setRelecture] = useState<string | null>(null)
  const [voirAnnexes, setVoirAnnexes] = useState(false)
  // Plusieurs rapports d'une matinée d'entretien s'écartent ensemble, avec un motif.
  const selection = useSelectionDocs()

  const plafond = fournisseur === null ? PLAFOND : PLAFOND_FOURNISSEUR
  const res = useRessource<DocRecu[]>(`/api/v1/finance/sources?limit=${plafond}`, lireDocuments)
  const recus = useMemo(() => (res.etat.phase === 'ok' ? res.etat.data : []), [res.etat])
  // Les compteurs des vues comptent DANS le fournisseur choisi : « À vérifier
  // (3) » doit annoncer ce que la vue montrera, pas le total tous fournisseurs.
  const docs = useMemo(
    () => (fournisseur === null ? recus : recus.filter((d) => d.provider === fournisseur)),
    [recus, fournisseur],
  )

  const compte = (v: Vue) => docs.filter((d) => dansLaVue(d, v)).length
  const options: { value: Vue; label: string }[] = [
    { value: 'a_verifier', label: `À vérifier (${compte('a_verifier')})` },
    { value: 'importes', label: `Importés (${compte('importes')})` },
    { value: 'doublons', label: `Doublons (${compte('doublons')})` },
    { value: 'erreurs', label: `Erreurs (${compte('erreurs')})` },
    { value: 'tout', label: `Tout (${docs.length})` },
  ]

  // À l'arrivée, l'adresse est réécrite sous sa forme canonique : `zap`
  // devient `ZAP_SIS`, un fournisseur inconnu disparaît — l'adresse dit
  // toujours exactement ce que la liste montre.
  useEffect(() => { ecrireAdresse(vue, fournisseur) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Une NAVIGATION vers cet écran déjà affiché (menu « Sources reçues »,
  // palette, Retour vers une autre vue) ne le remonte pas : le chemin ne
  // change pas. L'adresse fait pourtant foi — on la relit à chaque
  // navigation, sans quoi « /comptabilite/sources » nu annonçait la liste par
  // défaut au-dessus d'une liste encore filtrée sur ZAP (revue du 07/10/2026).
  const navigations = useNavigationCount()
  const navigationVue = useRef(navigations)
  useEffect(() => {
    if (navigations === navigationVue.current) return
    navigationVue.current = navigations
    const v = vueDepuisUrl()
    const f = fournisseurDepuisUrl()
    setVue(v)
    setFournisseur(f)
    selection.vider()
    ecrireAdresse(v, f)
  }, [navigations]) // eslint-disable-line react-hooks/exhaustive-deps

  /** La vue vit dans l'URL : un lien envoyé à un collègue rouvre la même liste. */
  const changerVue = (v: Vue) => {
    setVue(v)
    selection.vider()
    ecrireAdresse(v, fournisseur)
  }

  /** Retirer la puce : toute la liste revient, dans la vue en cours. */
  const retirerFournisseur = () => {
    setFournisseur(null)
    selection.vider()
    ecrireAdresse(vue, null)
  }

  /**
   * Relire n'efface pas la lecture précédente : elle en AJOUTE une. Le rôle
   * applicatif n'a ni UPDATE ni DELETE sur les lectures, et c'est ce qui permet
   * de savoir, dans deux ans, avec quelle logique un vieux document a été
   * interprété le jour où il a servi à arrêter une recette.
   */
  const relire = async (d: DocRecu) => {
    setRelecture(d.id)
    const r = await adminMutate<{ error?: string; message?: string; status?: string }>(
      'POST', `/api/v1/finance/sources/${d.id}/parse`,
    )
    setRelecture(null)
    if (r.status >= 200 && r.status < 300) {
      toast('ok', r.data.status === 'PARSED'
        ? 'Document relu : tout a été compris cette fois.'
        : 'Document relu. Il reste à vérifier — le détail dit ce qui bloque.')
      res.recharger()
      return
    }
    toast('error', r.data.message ?? 'La relecture a été refusée par le serveur.')
  }

  const visibles = docs.filter((d) => dansLaVue(d, vue))
  const fiches = visibles.filter(aTraiter)
  // Les annexes ZAP (une vingtaine par nuit) sont des preuves : repliées par
  // défaut, elles ne noient plus les rapports d'argent (retour du 07/10/2026).
  const toutesLignes = visibles.filter((d) => !aTraiter(d))
  const nbAnnexes = toutesLignes.filter((d) => d.annexe !== null).length
  const lignes = voirAnnexes ? toutesLignes : toutesLignes.filter((d) => d.annexe === null)
  const enAttente = visibles.filter((d) => d.etat === 'RECEIVED' || d.etat === 'PARSING').length
  const choisis = docs.filter((d) => selection.a(d.id)).map((d) => ({ id: d.id, ecarte: d.ecarte }))
  const tousLignes = lignes.length > 0 && lignes.every((d) => selection.a(d.id))

  return (
    <main className="lc-page lc-page--large lc-stagger">
      <SmartBack />

      <header className="fr-hero">
        <div>
          <h1 className="lc-title"><FileSearch size={26} aria-hidden="true" /> Sources reçues</h1>
          <p className="lc-subtitle">
            Les rapports de caisse et de billetterie tels qu’ils sont arrivés.
            Chaque document reste ici avec son empreinte : c’est la pièce, pas sa
            copie.
          </p>
        </div>
        <div className="fr-hero__actions">
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon"
            onClick={res.recharger} aria-label="Recharger la liste des documents">
            <RotateCcw size={16} />
          </button>
        </div>
      </header>

      {peutGerer ? <DepotRapports onFini={res.recharger} /> : null}

      <div className="fis-filtres">
        <SegmentedControl<Vue> value={vue} options={options} onChange={changerVue} ariaLabel="Filtrer les documents reçus" />
        {fournisseur !== null ? (
          <span className="fis-puce">
            <span className="fis-puce__cle">Fournisseur</span>
            <span className="fis-puce__val">{libelleProvenance(fournisseur)}</span>
            <button type="button" className="fis-puce__retirer" onClick={retirerFournisseur}
              aria-label={`Retirer le filtre ${libelleProvenance(fournisseur)} : afficher tous les fournisseurs`}>
              <X size={14} aria-hidden="true" />
            </button>
          </span>
        ) : null}
        <span className="fr-toolbar__spacer" />
        <span className="fr-mini">
          {fournisseur === null
            ? `Les ${plafond} documents les plus récents, ceux sans nuit d’abord.`
            : `Recherche faite parmi les ${plafond} documents les plus récents, tous fournisseurs confondus.`}
        </span>
      </div>

      <EtatBloc etat={res.etat} quoi="les documents reçus" />

      {res.etat.phase === 'ok' && visibles.length === 0 ? (
        <div className="fi-empty">
          <div className="fi-empty__title">
            {vue === 'a_verifier' ? 'Aucun document n’attend de vérification' : 'Aucun document dans cette vue'}
            {fournisseur !== null ? ` pour ${libelleProvenance(fournisseur)}` : ''}
          </div>
          <p>
            {vue === 'a_verifier'
              ? 'Tout ce qui est arrivé a été lu. Un document que Gestion ne saura pas lire apparaîtra ici, avec sa raison.'
              : 'Rien à afficher pour ce filtre parmi les documents les plus récents.'}
          </p>
          {fournisseur !== null ? (
            <button type="button" className="lc-btn lc-btn--sm" onClick={retirerFournisseur}>
              Afficher tous les fournisseurs
            </button>
          ) : null}
        </div>
      ) : null}

      {/* ═══════════ CE QUI ATTEND UNE DÉCISION ═══════════ */}
      {fiches.length > 0 ? (
        <section className="fr-section">
          <div className="fr-section__head">
            <h2 className="fr-section__title">À traiter — {fiches.length} document{fiches.length > 1 ? 's' : ''}</h2>
            <span className="fr-section__hint">Chacun porte la raison qui l’a arrêté.</span>
          </div>
          <div className="fis-fiches">
            {fiches.map((d) => (
              <article className="fis-fiche" key={d.id}>
                <div className="fis-fiche__head">
                  {peutGerer ? (
                    <Checkbox checked={selection.a(d.id)} onChange={() => selection.basculer(d.id)} ariaLabel={`Sélectionner ${d.fichier}`} />
                  ) : null}
                  <span className={pastilleEtat(d.etat)}>{libelleEtat(d.etat)}</span>
                  <h3 className="fis-fiche__nom">{d.fichier}</h3>
                  <span className="fr-mini">{libelleProvenance(d.provider)} · {libelleType(d.reportType)}</span>
                </div>

                <p className="fis-fiche__raison">{raisonCourte(d)}</p>

                <dl className="fis-faits">
                  <div className="fis-fait">
                    <dt>Nuit commerciale</dt>
                    <dd>{d.nuit === null ? <span className="fis-manque">Non rattaché</span> : nuitCourte(d.nuit)}</dd>
                  </div>
                  <div className="fis-fait">
                    <dt>Reçu</dt>
                    <dd>{d.recuLe === null ? '—' : `${ilYA(d.recuLe)} · ${heureParis(d.recuLe)}`}</dd>
                  </div>
                  <div className="fis-fait">
                    <dt>Anomalies</dt>
                    <dd>{d.anomalies === null ? '—' : String(d.anomalies)}</dd>
                  </div>
                  <div className="fis-fait">
                    <dt>Origine</dt>
                    <dd><Origine doc={d} /></dd>
                  </div>
                </dl>

                <div className="fr-actions">
                  <VoirSource doc={visible(d)} className="lc-btn lc-btn--primary">Voir le PDF</VoirSource>
                  <Link className="lc-btn" href={`/comptabilite/sources/${d.id}`}>
                    Fiche du document
                  </Link>
                  {peutGerer ? (
                    <button type="button" className="lc-btn" disabled={relecture === d.id}
                      onClick={() => { void relire(d) }}>
                      {relecture === d.id ? 'Relecture en cours' : 'Relire'}
                    </button>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {/* ═══════════ CE QUI EST DÉJÀ LU : UNE LIGNE CHACUN ═══════════ */}
      {lignes.length > 0 || nbAnnexes > 0 ? (
        <section className="fr-section">
          <div className="fr-section__head">
            <h2 className="fr-section__title">Documents lus — {lignes.length}</h2>
            {enAttente > 0 ? (
              <span className="fr-section__hint">
                Dont {enAttente} reçu{enAttente > 1 ? 's' : ''} et pas encore lu{enAttente > 1 ? 's' : ''}.
              </span>
            ) : null}
            <span className="fr-toolbar__spacer" />
            {nbAnnexes > 0 ? (
              <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => setVoirAnnexes((v) => !v)}>
                {voirAnnexes ? `Masquer les ${nbAnnexes} annexes ZAP` : `Afficher les ${nbAnnexes} annexes ZAP : doseurs, cartes, tiroirs`}
              </button>
            ) : null}
          </div>
          <div className="fi-tablewrap">
            <table className="fi-table fis-table">
              <thead>
                <tr>
                  {peutGerer ? (
                    <th scope="col" className="fis-choix">
                      <Checkbox checked={tousLignes} onChange={(on) => selection.poser(lignes.map((d) => d.id), on)}
                        ariaLabel="Sélectionner tous les documents de la liste" />
                    </th>
                  ) : null}
                  <th scope="col">Reçu</th>
                  <th scope="col">Provenance</th>
                  <th scope="col">Type de rapport</th>
                  <th scope="col">Nuit</th>
                  <th scope="col">Fichier</th>
                  <th scope="col">État</th>
                  <th scope="col">Analyseur</th>
                  <th scope="col">Anomalies</th>
                  <th scope="col">Origine</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((d) => (
                  <tr key={d.id} className={selection.a(d.id) ? 'fis-ligne--choisie' : undefined}>
                    {peutGerer ? (
                      <td className="fis-choix">
                        <Checkbox checked={selection.a(d.id)} onChange={() => selection.basculer(d.id)} ariaLabel={`Sélectionner ${d.fichier}`} />
                      </td>
                    ) : null}
                    <td>
                      {d.recuLe === null ? '—' : heureParis(d.recuLe)}
                      <span className="fr-cell-sub">{d.recuLe === null ? 'Date non transmise' : ilYA(d.recuLe)}</span>
                    </td>
                    <td>{libelleProvenance(d.provider)}</td>
                    <td>{typeAffiche(d)}</td>
                    <td>{d.nuit === null ? <span className="fis-manque">Non rattaché</span> : nuitCourte(d.nuit)}</td>
                    <td className="fr-cell-main">
                      <VoirSource doc={visible(d)} liste={lignes.map(visible)} className="fv-lien fis-lien" />
                    </td>
                    <td>
                      {d.annexe !== null
                        ? <span className="lc-pill lc-pill--idle">{d.annexe === 'AUTRE' ? 'Conservée' : 'Lue'}</span>
                        : <span className={pastilleEtat(d.etat)}>{libelleEtat(d.etat)}</span>}
                    </td>
                    <td className="fis-tech">{d.analyseur ?? '—'}</td>
                    <td className="fr-cell-num">{d.anomalies === null ? '—' : d.anomalies}</td>
                    <td><Origine doc={d} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <BarreEcart choisis={choisis} onVider={selection.vider} onFait={res.recharger} />
    </main>
  )
}

/**
 * D'OÙ VIENT CE DOCUMENT. Un mail d'origine se dit avec son expéditeur ; à
 * défaut, avec la personne qui a déposé le fichier. Quand rien n'est connu, on
 * écrit « — » : ce n'est pas un document venu de nulle part, c'est une
 * information qui n'est pas remontée jusqu'ici.
 */
function Origine({ doc }: { doc: DocRecu }) {
  if (doc.ecarte) {
    return <span className="fis-origine" title={doc.motifEcart ?? undefined}>Écarté{doc.motifEcart ? ` : ${doc.motifEcart}` : ''}</span>
  }
  if (doc.mail !== null) {
    const titre = doc.mail.sujet ?? doc.mail.identifiant ?? 'Message sans objet'
    return (
      <span className="fis-origine" title={titre}>
        <Mail size={14} aria-hidden="true" />
        {doc.mail.expediteur ?? 'Boîte mail des sources'}
      </span>
    )
  }
  if (doc.deposePar !== null) return <span className="fis-origine">Déposé par {doc.deposePar}</span>
  return <span className="fis-manque">—</span>
}
